import { useState } from "react";
import axios from "axios";
import { CreditCard, Landmark, Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentBadge({ payment }) {
  const s = payment?.status || "unpaid";
  const map = {
    paid: ["Paid · card", "bg-emerald-50 text-emerald-700 border-emerald-200"],
    pending: ["Card payment in progress", "bg-sky-50 text-sky-700 border-sky-200"],
    invoice_requested: ["Invoice requested · bank transfer", "bg-amber-50 text-amber-700 border-amber-200"],
    unpaid: ["Payment due", "bg-slate-100 text-slate-600 border-slate-200"],
  };
  const [label, cls] = map[s] || map.unpaid;
  return <span data-testid="payment-status-badge" data-status={s} className={`inline-block font-tech text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm ${cls}`}>{label}</span>;
}

export default function PaymentOptions({ orderId, email, payment, total, onUpdate }) {
  const [busy, setBusy] = useState("");
  const status = payment?.status || "unpaid";

  const payCard = async () => {
    setBusy("card");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { order_id: orderId, email, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start card payment");
      setBusy("");
    }
  };

  const requestInvoice = async () => {
    setBusy("bank");
    try {
      await axios.post(`${API}/payments/bank-transfer`, { order_id: orderId, email });
      toast.success("Invoice requested — wire/ACH details will be emailed within 1 business day");
      onUpdate?.({ status: "invoice_requested", method: "bank_transfer" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request an invoice");
    } finally {
      setBusy("");
    }
  };

  if (status === "paid") {
    return (
      <div className="flex items-center gap-3 border border-emerald-200 bg-emerald-50 p-4" data-testid="payment-paid-notice">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-emerald-800">Payment received — ${Number(payment.amount ?? total).toFixed(2)} by card</p>
          <p className="text-xs text-emerald-700 mt-0.5">Your order moves to batch verification and cold-pack packaging.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="payment-options">
      {status === "invoice_requested" && (
        <div className="flex items-start gap-3 border border-amber-200 bg-amber-50 p-4" data-testid="payment-invoice-notice">
          <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">Invoice requested. Our desk emails a commercial invoice with wire / ACH instructions within 1 business day. You can still pay by card below.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={payCard} disabled={!!busy} data-testid="pay-with-card-btn"
          className="group text-left border border-slate-900 bg-slate-900 hover:bg-sky-700 hover:border-sky-700 text-white p-4 rounded-2xl transition-colors disabled:opacity-60">
          <div className="flex items-center justify-between">
            <CreditCard className="h-5 w-5" />
            {busy === "card" ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="font-tech text-[10px] uppercase tracking-[0.16em] text-slate-300 group-hover:text-white">Secure · Stripe</span>}
          </div>
          <p className="font-display font-semibold mt-3">Pay ${Number(total).toFixed(2)} by card</p>
          <p className="text-xs text-slate-300 mt-1">Visa, Mastercard, Amex. Receipt emailed instantly.</p>
        </button>
        <button onClick={requestInvoice} disabled={!!busy || status === "invoice_requested"} data-testid="pay-by-invoice-btn"
          className="text-left border border-slate-300 bg-white hover:border-slate-900 text-slate-900 p-4 rounded-2xl transition-colors disabled:opacity-60">
          <div className="flex items-center justify-between">
            <Landmark className="h-5 w-5 text-slate-700" />
            {busy === "bank" ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="font-tech text-[10px] uppercase tracking-[0.16em] text-slate-400">Net terms</span>}
          </div>
          <p className="font-display font-semibold mt-3">{status === "invoice_requested" ? "Invoice requested" : "Pay by invoice / bank transfer"}</p>
          <p className="text-xs text-slate-500 mt-1">Commercial invoice with wire / ACH details, emailed within 1 business day.</p>
        </button>
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        Payment confirms a research-supply purchase under your signed Research Purchaser Agreement. Materials are for in-vitro laboratory research use only.
      </p>
    </div>
  );
}
